/**
 * Theme contrast model: the canonical foreground/background pairings the UI
 * relies on, with the WCAG 2.1 relative-luminance maths to measure them. This
 * is the single source of truth for the contrast unit test (which fails CI on
 * any regression) and the optional CLI printer.
 *
 * Values mirror index.css ([data-theme="dark"] / [data-theme="light"]) and
 * brand-tokens.css. Translucent dark washes are alpha-composited over their
 * surface so the measured number is what the eye actually sees.
 *
 * Levels:
 *  - TEXT (4.5): normal text on its background.
 *  - UI (3.0): a non-text element's fill against the surface behind it
 *    (WCAG 1.4.11) e.g. a status badge must be seen as a distinct pill.
 *  - STATE_DELTA (ΔE): a hover/selected state must be perceptibly different from
 *    its resting surface. Measured as CIE76 ΔE, NOT a WCAG ratio: a state can
 *    shift hue at the same lightness (a volt wash on a grey card) where the
 *    luminance ratio barely moves yet the eye sees it plainly. ΔE catches that,
 *    and still catches a state that lands on an identical colour (ΔE 0, the
 *    menu-hover bug).
 */

export const TEXT = 4.5;
export const UI = 3.0;

const toRgb = (hex: string): [number, number, number] => {
  const h = hex.replace('#', '');
  return [parseInt(h.slice(0, 2), 16), parseInt(h.slice(2, 4), 16), parseInt(h.slice(4, 6), 16)];
};

/** Alpha-composite a colour over an opaque base (what a translucent wash shows). */
export const compositeOver = (hex: string, alpha: number, baseHex: string): string => {
  const f = toRgb(hex);
  const b = toRgb(baseHex);
  const o = f.map((c, i) => Math.round(c * alpha + b[i] * (1 - alpha)));
  return `#${o.map((x) => x.toString(16).padStart(2, '0')).join('')}`;
};

export const relLum = (hex: string): number => {
  const [r, g, b] = toRgb(hex).map((v) => {
    const s = v / 255;
    return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
  });
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
};

export const contrastRatio = (a: string, b: string): number => {
  const l1 = relLum(a);
  const l2 = relLum(b);
  return (Math.max(l1, l2) + 0.05) / (Math.min(l1, l2) + 0.05);
};

/** Retain a color's hue while moving toward a readable foreground or fill. */
export function readableColor(color: string, target: string, backgrounds: string[], minimum = TEXT): string {
  for (let step = 0; step <= 100; step++) {
    const candidate = compositeOver(target, step / 100, color);
    if (backgrounds.every(background => contrastRatio(candidate, background) >= minimum)) return candidate;
  }
  return target;
}

/** CIE76 perceptual colour distance (ΔE) in LAB. Unlike the WCAG ratio (luminance
 *  only) this sees hue + chroma, so a state that shifts colour at the same
 *  lightness still registers. Used for the hover/selected perceptibility floor. */
const labOf = (hex: string): [number, number, number] => {
  const [r, g, b] = toRgb(hex).map((v) => {
    const s = v / 255;
    return s <= 0.04045 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
  });
  const x = (r * 0.4124 + g * 0.3576 + b * 0.1805) / 0.95047;
  const y = r * 0.2126 + g * 0.7152 + b * 0.0722;
  const z = (r * 0.0193 + g * 0.1192 + b * 0.9505) / 1.08883;
  const f = (t: number): number => (t > 0.008856 ? Math.cbrt(t) : 7.787 * t + 16 / 116);
  const [fx, fy, fz] = [f(x), f(y), f(z)];
  return [116 * fy - 16, 500 * (fx - fy), 200 * (fy - fz)];
};
export const deltaE = (a: string, b: string): number => {
  const [l1, a1, b1] = labOf(a);
  const [l2, a2, b2] = labOf(b);
  return Math.sqrt((l1 - l2) ** 2 + (a1 - a2) ** 2 + (b1 - b2) ** 2);
};

/** Machado, Oliveira and Fernandes (2009) full-severity matrices in linear RGB. */
const CVD_MATRICES = {
  protan: [[0.152286, 1.052583, -0.204868], [0.114503, 0.786281, 0.099216], [-0.003882, -0.048116, 1.051998]],
  deutan: [[0.367322, 0.860646, -0.227968], [0.280085, 0.672501, 0.047413], [-0.011820, 0.042940, 0.968881]],
} as const;

/** How a colour appears with protanopia or deuteranopia, as hex. Used to check chart palettes stay distinguishable. */
export const simulateColorBlindness = (hex: string, deficiency: keyof typeof CVD_MATRICES): string => {
  const linear = toRgb(hex).map((v) => {
    const s = v / 255;
    return s <= 0.04045 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
  });
  const channels = CVD_MATRICES[deficiency].map((row) => {
    const value = Math.min(1, Math.max(0, row[0] * linear[0] + row[1] * linear[1] + row[2] * linear[2]));
    const encoded = value <= 0.0031308 ? value * 12.92 : 1.055 * value ** (1 / 2.4) - 0.055;
    return Math.round(encoded * 255);
  });
  return `#${channels.map((x) => x.toString(16).padStart(2, '0')).join('')}`;
};

/** A hover/selected state must differ from its resting surface by at least this
 *  ΔE (CIE76). ~2.3 is one JND; 4 keeps a state clearly visible while allowing a
 *  same-family tint shift (a deeper volt wash on a volt-wash card). */
export const STATE_DELTA = 4;

interface Theme {
  background: string; surface: string; raised: string; hover: string; muted: string;
  fg: string; mutedFg: string; subtleFg: string; ink: string;
  /** [fill, label] per status badge. */
  badge: Record<string, [string, string]>;
  /** [fill, textColour] for callout washes. */
  wash: Record<string, [string, string]>;
  /** The deeper success/flow wash used as the mint review card's hover/selected
   *  fill. Hosts the card's own body + secondary text and the generated-check
   *  icon (tertiary is NOT placed on it: it misses UI in dark, which is why the
   *  meta pills use the darker {@link washSuccessDeep} instead). */
  washSuccessHover: string;
  /** The darker-green PILL fill on a mint review card. Hosts every meta pill's
   *  text (category/author, tag, the holito stamp) and the ghost-button icon on
   *  hover, dark enough that all of them clear contrast in BOTH themes where the
   *  lighter card wash would not. */
  washSuccessDeep: string;
  /** soft accent text colour on a surface (volt-soft in dark, volt-dark in light). */
  accentText: string;
  successSoft: string; warningSoft: string; dangerSoft: string;
  /** solid fill + on-fill label for the four states. */
  fills: Record<string, [string, string]>;
}

const DARK_SURFACE = '#18181C';
const DARK: Theme = {
  background: '#0A0A0D', surface: DARK_SURFACE, raised: '#212126', hover: '#2A2A30', muted: '#212126',
  fg: '#F4F4F7', mutedFg: '#ABABB2', subtleFg: '#7E7E86', ink: '#0D0B1E',
  badge: {
    neutral: ['#ABABB2', '#0A0A0D'],
    warning: ['#F5A623', '#0D0B1E'],
    success: ['#00F5A0', '#0D0B1E'],
    accent: ['#A88FFF', '#0D0B1E'],
    danger: ['#FF8A8A', '#0D0B1E'],
  },
  wash: {
    accent: [compositeOver('#6E3FFF', 0.16, DARK_SURFACE), '#F4F4F7'],
    success: [compositeOver('#00F5A0', 0.14, DARK_SURFACE), '#F4F4F7'],
    warning: [compositeOver('#FF6B35', 0.16, DARK_SURFACE), '#F4F4F7'],
    danger: [compositeOver('#FF5C5C', 0.14, DARK_SURFACE), '#F4F4F7'],
  },
  washSuccessHover: compositeOver('#00F5A0', 0.22, DARK_SURFACE),
  washSuccessDeep: '#08392a',
  accentText: '#A88FFF', successSoft: '#00F5A0', warningSoft: '#FFC04D', dangerSoft: '#FF8A8A',
  fills: {
    accent: ['#6E3FFF', '#FFFFFF'],
    success: ['#00F5A0', '#0D0B1E'],
    warning: ['#F5A623', '#0D0B1E'],
    danger: ['#DC2626', '#FFFFFF'],
  },
};

const LIGHT: Theme = {
  background: '#FFFFFF', surface: '#F2EEFF', raised: '#FFFFFF', hover: '#E2D9FF', muted: '#F0F0F4',
  fg: '#1A1A22', mutedFg: '#5A5A70', subtleFg: '#7C7C94', ink: '#0D0B1E',
  badge: {
    neutral: ['#5A5A70', '#F7F7FA'],
    warning: ['#E65520', '#0D0B1E'],
    success: ['#1f7d35', '#FFFFFF'],
    accent: ['#6E3FFF', '#FFFFFF'],
    danger: ['#DD3333', '#FFFFFF'],
  },
  wash: {
    accent: ['#F2EEFF', '#1A1A22'],
    success: ['#E7FBF3', '#1A1A22'],
    warning: ['#FFF1EA', '#1A1A22'],
    danger: ['#FDECEC', '#1A1A22'],
  },
  washSuccessHover: '#D6F7EA',
  washSuccessDeep: '#c5f3e2',
  accentText: '#5929E8', successSoft: '#047857', warningSoft: '#E65520', dangerSoft: '#C22222',
  fills: {
    accent: ['#6E3FFF', '#FFFFFF'],
    success: ['#1f7d35', '#FFFFFF'],
    warning: ['#FF6B35', '#0D0B1E'],
    danger: ['#DD3333', '#FFFFFF'],
  },
};

/** Resolved surface and text values per theme, mirrored from index.css. */
export const THEMES = { dark: DARK, light: LIGHT } as const;

export interface ContrastPair {
  theme: 'dark' | 'light';
  label: string;
  fg: string;
  bg: string;
  level: number;
}

const pairsFor = (name: 'dark' | 'light', t: Theme): ContrastPair[] => {
  const p = (label: string, fg: string, bg: string, level: number): ContrastPair => ({ theme: name, label, fg, bg, level });
  const out: ContrastPair[] = [];

  // The full matrix: every neutral text role against every opaque surface it can
  // land on. Surfaces include the resting planes AND the interactive states
  // (hover, and the brand washes, which are now used as hover / selected fills,
  // not only as callouts). A wash that hosts a card's text must clear contrast
  // for that text, which is exactly the case the callout-only model used to miss.
  const surfaces: Array<[string, string]> = [
    ['page', t.background],
    ['card', t.surface],
    ['raised', t.raised],
    ['hover', t.hover],
    ['muted', t.muted],
    ['wash-accent', t.wash.accent[0]],
    ['wash-success', t.wash.success[0]],
    ['wash-warning', t.wash.warning[0]],
    ['wash-danger', t.wash.danger[0]],
  ];
  // body + secondary are real copy (TEXT 4.5); tertiary is de-emphasised meta,
  // held to UI (3.0). If a role sits on a surface, it must clear its level there.
  const roles: Array<[string, string, number]> = [
    ['body', t.fg, TEXT],
    ['secondary', t.mutedFg, TEXT],
    ['tertiary', t.subtleFg, UI],
  ];
  for (const [sName, bg] of surfaces) {
    for (const [rName, color, level] of roles) {
      out.push(p(`${rName} on ${sName}`, color, bg, level));
    }
  }

  // Soft accent / status TEXT emphasis on a card.
  out.push(p('accent text on card', t.accentText, t.surface, TEXT));
  // The "made by holito" stamp on a normal gallery card is a page-colour pill with
  // volt text (the volt WASH would be invisible: surface == wash-accent in light).
  out.push(p('accent stamp on page pill', t.accentText, t.background, TEXT));
  out.push(p('success text on card', t.successSoft, t.surface, TEXT));
  out.push(p('warning text on card', t.warningSoft, t.surface, UI));
  out.push(p('danger text on card', t.dangerSoft, t.surface, TEXT));

  // The mint review card's hover/selected fill (wash-success-hover) hosts the
  // card's own body + secondary text and the generated-check icon. Tertiary is
  // NOT listed: it misses UI on this wash in dark, which is why the meta pills sit
  // on the darker wash-success-deep instead (next).
  out.push(p('body on wash-success-hover', t.fg, t.washSuccessHover, TEXT));
  out.push(p('secondary on wash-success-hover', t.mutedFg, t.washSuccessHover, TEXT));
  out.push(p('success check on wash-success-hover', t.successSoft, t.washSuccessHover, UI));

  // The mint review card's PILL fill (wash-success-deep): a deeper green carrying
  // every meta pill's text (category/author muted, tag subtle, the holito stamp's
  // success-soft) plus the foreground ghost-button icon on hover. The lighter card
  // wash is too pale for the grey text in dark, so this token darkens until all of
  // them clear the contract in BOTH themes.
  out.push(p('ghost icon on wash-success-deep', t.fg, t.washSuccessDeep, TEXT));
  out.push(p('category/author on wash-success-deep', t.mutedFg, t.washSuccessDeep, TEXT));
  out.push(p('tag on wash-success-deep', t.subtleFg, t.washSuccessDeep, UI));
  out.push(p('holito stamp on wash-success-deep', t.successSoft, t.washSuccessDeep, TEXT));

  // The coloured icon / emphasis on each wash (a callout or selected item carries
  // its meaning through this icon + the tint) must be seen against its own wash.
  const softOnWash: Record<string, string> = {
    accent: t.accentText, success: t.successSoft, warning: t.warningSoft, danger: t.dangerSoft,
  };
  for (const [k, [bg]] of Object.entries(t.wash)) {
    out.push(p(`${k} icon on wash`, softOnWash[k], bg, UI));
  }

  // Solid status fills, and status badges (visible pill + readable label).
  for (const [k, [fill, label]] of Object.entries(t.fills)) {
    out.push(p(`solid ${k} label`, label, fill, TEXT));
  }
  for (const [k, [fill, label]] of Object.entries(t.badge)) {
    out.push(p(`badge ${k} fill vs card`, fill, t.surface, UI));
    out.push(p(`badge ${k} label`, label, fill, TEXT));
  }
  return out;
};

/** Every contrast pair the design system must uphold, both themes. */
export const auditPairs = (): ContrastPair[] => [...pairsFor('dark', DARK), ...pairsFor('light', LIGHT)];

/** A hover/selected state and the resting surface it replaces. Checked by ΔE, not
 *  a contrast ratio: the state only has to be SEEN as different, and it may shift
 *  hue at the same lightness (a deeper volt wash over a volt-wash card). */
export interface StatePair { theme: 'dark' | 'light'; label: string; state: string; rest: string; }
const statesFor = (name: 'dark' | 'light', t: Theme): StatePair[] => [
  { theme: name, label: 'hover vs card', state: t.hover, rest: t.surface },
  { theme: name, label: 'hover vs raised', state: t.hover, rest: t.raised },
  // The mint review card's hover/selected step must be seen against its resting
  // mint wash (same family, deeper tint: ΔE, not a luminance ratio).
  { theme: name, label: 'wash-success-hover vs wash-success', state: t.washSuccessHover, rest: t.wash.success[0] },
  // The pill (deep green) must read as a distinct pill on BOTH the resting and the
  // hovered mint card.
  { theme: name, label: 'wash-success-deep vs wash-success (rest card)', state: t.washSuccessDeep, rest: t.wash.success[0] },
  { theme: name, label: 'wash-success-deep vs wash-success-hover (hover card)', state: t.washSuccessDeep, rest: t.washSuccessHover },
  // A normal card's pills are the PAGE colour, which must read as a distinct pill
  // on BOTH the resting card (surface) and the hovered card (surface-hover) -- the
  // latter is the bug a bg-surface-hover pill hits (it becomes the hovered card).
  { theme: name, label: 'card pill (page) vs surface (rest card)', state: t.background, rest: t.surface },
  { theme: name, label: 'card pill (page) vs surface-hover (hover card)', state: t.background, rest: t.hover },
];
/** Every resting -> hover/selected transition the design system must keep visible. */
export const auditStates = (): StatePair[] => [...statesFor('dark', DARK), ...statesFor('light', LIGHT)];
